import React from "react";
import * as d3 from "d3";
import moment from "moment";
import { onNextFrame, formatNumber } from "../../utils/Functions";
import { Device } from "../../utils/Device";
import "./ForecastGraph.scss";
function getMiddleDate(minDate, maxDate) {
    const timeDifference = (maxDate.getTime() - minDate.getTime()) / 2;
    const middleDay = minDate.getTime() + timeDifference;
    return new Date(middleDay);
}
function getUpperBound(current, multiple) {
    if (current === 0) {
        return multiple;
    }
    return Math.ceil(current / multiple) * multiple;
}
const MONTHLY_STOCK_RETURN = 0.07 / 12;
const MONTHLY_BOND_RETURN = 0.015 / 12;
const ASSETS_CORRELATION = 0.1;
const MONTHLY_STANDARD_DEVIATION_STOCKS = 0.15 / Math.sqrt(12);
const MONTHLY_STANDARD_DEVIATION_BONDS = 0.02 / Math.sqrt(12);
const MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED = Math.pow(MONTHLY_STANDARD_DEVIATION_STOCKS, 2);
const MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED = Math.pow(MONTHLY_STANDARD_DEVIATION_BONDS, 2);
const PORTFOLIO_VOLATILITY = MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED +
    MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED -
    2 *
        MONTHLY_STANDARD_DEVIATION_STOCKS *
        MONTHLY_STANDARD_DEVIATION_BONDS *
        ASSETS_CORRELATION;
const TEN_PERCENT_CONFIDENCE = 1.2816;
const FOURTY_PERCENT_CONFIDENCE = 0.2533;
const plotRightMarginMobile = 0;
const PLOT_BOTTOM_MARGIN = 22;
const PLOT_TOP_MARGIN = 0;
export class ForecastGraph extends React.Component {
    constructor(props) {
        super(props);
        this.redraw = (props) => {
            if (this.unmounted) {
                return false;
            }
            const clone = Object.assign({}, props);
            onNextFrame(() => {
                this.data = this.generatePoints(clone);
                this.data.extremeValues = this.generateMinAndMax(clone);
                this.update();
            });
            return false;
        };
        this.hideHoverElements = () => {
            this.d3VeryBadMarker && this.d3VeryBadMarker.style("display", "none");
            this.d3BadMarker && this.d3BadMarker.style("display", "none");
            this.d3GoodMarker && this.d3GoodMarker.style("display", "none");
            this.d3VeryGoodMarker && this.d3VeryGoodMarker.style("display", "none");
            this.d3Ruler && this.d3Ruler.style("display", "none");
            this.tooltip && (this.tooltip.style.opacity = "0");
        };
        this.showHoverElements = () => {
            this.d3VeryBadMarker && this.d3VeryBadMarker.style("display", null);
            this.d3BadMarker && this.d3BadMarker.style("display", null);
            this.d3GoodMarker && this.d3GoodMarker.style("display", null);
            this.d3VeryGoodMarker && this.d3VeryGoodMarker.style("display", null);
            this.d3Ruler && this.d3Ruler.style("display", null);
            this.tooltip && (this.tooltip.style.opacity = "1");
        };
        this.plot = React.createRef();
        this.graphTooltipShortInterval = React.createRef();
        this.graphTooltipInner = React.createRef();
        this.unmounted = false;
        this.hasAddedInputEvents = false;
        this.areas = { w: 0, h: 0 };
        this.tooltip = null;
        this.tooltipIntervalWarning = null;
        this.tooltipInner = null;
        this.redraw(props);
    }
    shouldComponentUpdate(nextProps) {
        return this.redraw(nextProps);
    }
    componentWillUnmount() {
        this.unmounted = true;
    }
    componentDidMount() {
        window.addEventListener("resize", () => {
            this.redraw(this.props);
        });
        window.addEventListener("orientationchange", () => {
            this.redraw(this.props);
        });
    }
    render() {
        return (React.createElement("div", { ref: this.plot, className: "plot forecast-graph" },
            React.createElement("div", { className: "forecast-tooltip", style: { opacity: 0 } },
                React.createElement("span", { className: "forecast-tooltip-arrow-border" }),
                React.createElement("div", { className: "graph-tooltip-short-interval", style: { opacity: 1 }, ref: this.graphTooltipShortInterval }, "F\u00F6r tidsperioder under ett \u00E5r estimeras inte utveckling."),
                React.createElement("div", { className: "graph-tooltip-inner", style: { opacity: 1 }, ref: this.graphTooltipInner },
                    React.createElement("div", { className: "forecast-tooltip-header" },
                        "Bed\u00F6md sannolikhet att du om ",
                        React.createElement("span", { className: "tooltip-years" }),
                        React.createElement("span", { className: "tooltip-months" }),
                        " har:"),
                    React.createElement("ul", null,
                        React.createElement("li", { className: "forecast-tooltip-very-good" },
                            React.createElement("div", null,
                                React.createElement("span", { className: "very-good-marker" }),
                                React.createElement("span", { className: "marker-value" }))),
                        React.createElement("li", { className: "forecast-tooltip-good" },
                            React.createElement("div", null,
                                React.createElement("span", { className: "good-marker" }),
                                React.createElement("span", { className: "marker-value" }))),
                        React.createElement("li", { className: "forecast-tooltip-bad" },
                            React.createElement("div", null,
                                React.createElement("span", { className: "bad-marker" }),
                                React.createElement("span", { className: "marker-value" }))),
                        React.createElement("li", { className: "forecast-tooltip-very-bad" },
                            React.createElement("div", null,
                                React.createElement("span", { className: "very-bad-marker" }),
                                React.createElement("span", { className: "marker-value" }))))),
                React.createElement("span", { className: "forecast-tooltip-arrow" })),
            React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", className: "plot-graph" })));
    }
    updateArea() {
        const isInit = !this.d3Plot;
        if (isInit) {
            const d3PlotWrapper = d3.select(this.plot.current);
            this.d3Plot = d3PlotWrapper.select("svg");
            this.areas = { w: 0, h: 0 };
        }
        if (!this.d3Plot) {
            return;
        }
        let plotRef = this.plot.current;
        if (!plotRef || !plotRef.parentElement) {
            this.areas = { w: 0, h: 0 };
            return;
        }
        if (this.plot.current === null ||
            this.plot.current.parentElement === null) {
            return;
        }
        let box = this.plot.current.parentElement.getBoundingClientRect();
        let w = box.width;
        let h = box.height;
        this.areas.w = w;
        this.areas.h = h - PLOT_BOTTOM_MARGIN - PLOT_TOP_MARGIN;
        let rightMargin = plotRightMarginMobile;
        if (this.areas.w <= 0) {
            return;
        }
        this.plot.current.style.width = w + "px";
        this.plot.current.style.height = h + "px";
        this.tooltip = this.plot.current.querySelector(".forecast-tooltip");
        this.d3Plot.select("defs").remove();
        let gradient = this.d3Plot
            .append("defs")
            .append("linearGradient")
            .attr("id", "gradient")
            .attr("x1", "0%")
            .attr("y1", "-200%")
            .attr("x2", "0%")
            .attr("y2", "100%")
            .attr("spreadMethod", "pad");
        gradient
            .append("stop")
            .attr("offset", "20%")
            .attr("stop-color", "rgb(14,87,165)")
            .attr("stop-opacity", 0.8);
        gradient
            .append("stop")
            .attr("offset", "90%")
            .attr("stop-color", "rgb(6,114,229)")
            .attr("stop-opacity", 0.1);
        if (!isInit) {
            return;
        }
        this.d3Group = this.d3Plot
            .append("g")
            .attr("transform", "translate(0, " + PLOT_TOP_MARGIN + ")");
        this.d3Plot.append("text").attr("class", "plot-vertical-entity");
        this.tooltipIntervalWarning = this.graphTooltipShortInterval.current;
        this.tooltipInner = this.graphTooltipInner.current;
        this.d3Group
            .append("line")
            .attr("class", "border-bottom")
            .attr("x1", 0)
            .attr("x2", w - rightMargin)
            .attr("y1", this.areas.h)
            .attr("y2", this.areas.h);
        this.d3Group
            .append("line")
            .attr("class", "border-right")
            .attr("x1", w - rightMargin)
            .attr("x2", w - rightMargin)
            .attr("y1", 0)
            .attr("y2", this.areas.h);
        this.d3Group.append("path").attr("class", "full-band");
        this.d3Group.append("path").attr("class", "medium-band");
        this.d3Group.append("path").attr("class", "warning-band");
        this.d3Ruler = this.d3Group
            .append("line")
            .attr("x1", 0)
            .attr("x2", 0)
            .attr("class", "ruler")
            .style("display", "none");
        this.d3VeryBadMarker = this.d3Group
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 4)
            .attr("class", "very-bad-marker")
            .style("display", "none");
        this.d3BadMarker = this.d3Group
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 4)
            .attr("class", "bad-marker")
            .style("display", "none");
        this.d3GoodMarker = this.d3Group
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 4)
            .attr("class", "good-marker")
            .style("display", "none");
        this.d3VeryGoodMarker = this.d3Group
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", 4)
            .attr("class", "very-good-marker")
            .style("display", "none");
        this.d3Group.append("g").attr("class", "axis-x");
        this.d3Group.append("g").attr("class", "axis-y");
        this.d3Background = this.d3Group
            .append("rect")
            .on("mouseover", this.showHoverElements)
            .on("mouseout", this.hideHoverElements);
    }
    getBounds(maxProfit) {
        let t;
        if (maxProfit > 100000000) {
            t = getUpperBound(Math.ceil(maxProfit / 1000000), 100);
            return {
                min: 0,
                max: t,
                value: t * 1000000,
                multiple: 1000000,
                ext: "MSEK",
            };
        }
        if (maxProfit > 10000000) {
            t = getUpperBound(Math.ceil(maxProfit / 1000000), 10);
            return {
                min: 0,
                max: t,
                value: t * 1000000,
                multiple: 1000000,
                ext: "MSEK",
            };
        }
        if (maxProfit > 1000000) {
            t = getUpperBound(Math.ceil(maxProfit / 1000000), 1);
            return {
                min: 0,
                max: t,
                value: t * 1000000,
                multiple: 1000000,
                ext: "MSEK",
            };
        }
        if (maxProfit > 100000) {
            t = getUpperBound(Math.ceil(maxProfit / 1000), 100);
            return {
                min: 0,
                max: t,
                value: t * 1000,
                multiple: 1000,
                ext: "TSEK",
            };
        }
        t = getUpperBound(Math.ceil(maxProfit), 100);
        return {
            min: 0,
            max: t,
            value: t,
            multiple: 1,
            ext: "SEK",
        };
    }
    update() {
        if (this.unmounted) {
            return;
        }
        this.updateArea();
        if (this.areas.w <= 0 ||
            !this.data ||
            !this.tooltip ||
            !this.plot.current ||
            !this.d3Plot ||
            !this.d3Background ||
            !this.d3Ruler ||
            !this.d3Group) {
            return;
        }
        this.hideHoverElements();
        let plot = this.plot.current;
        let veryBadSeries = this.data.veryBadSeries;
        let badSeries = this.data.badSeries;
        let goodSeries = this.data.goodSeries;
        let veryGoodSeries = this.data.veryGoodSeries;
        let warningSeries = this.data.warningSeries;
        let dateToWorthMap = this.data.dateToWorthMap;
        let veryBadMarker = this.d3VeryBadMarker;
        let badMarker = this.d3BadMarker;
        let goodMarker = this.d3GoodMarker;
        let veryGoodMarker = this.d3VeryGoodMarker;
        let dataLength = badSeries.length;
        let tooltip = this.tooltip;
        let half = this.areas.w / 2;
        const isMobileView = Device.isMobile();
        let minDate = badSeries[0].date.toDate();
        let startingCapital = badSeries[0].worth;
        let maxDate = badSeries[dataLength - 1].date.toDate();
        let maxProfit = this.data.extremeValues.max;
        let areas = this.areas;
        let rightMargin = plotRightMarginMobile;
        let width = this.areas.w - rightMargin;
        let bounds = this.getBounds(maxProfit);
        let lowerBound = bounds.min;
        let upperBound = bounds.value;
        let middleBound = lowerBound + (upperBound - lowerBound) / 2;
        const showHoverElements = this.showHoverElements;
        let x = d3.scaleTime().range([0, width]);
        let y = d3.scaleLinear().range([this.areas.h, 0]);
        x.domain([minDate, maxDate]);
        y.domain([bounds.min, bounds.value]);
        let ticks = [];
        let midLowerBound = lowerBound + (middleBound - lowerBound) / 2;
        let midUpperBound = middleBound + (upperBound - middleBound) / 2;
        if (isMobileView) {
            ticks = [
                lowerBound,
                midLowerBound,
                middleBound,
                midUpperBound,
                upperBound,
            ];
        }
        else {
            ticks = [
                lowerBound,
                lowerBound + (midLowerBound - lowerBound) / 2,
                midLowerBound,
                midLowerBound + (middleBound - midLowerBound) / 2,
                middleBound,
                middleBound + (midUpperBound - middleBound) / 2,
                midUpperBound,
                midUpperBound + (upperBound - midUpperBound) / 2,
                upperBound,
            ];
        }
        let dates = [];
        if (Device.isMobile()) {
            dates = [minDate, getMiddleDate(minDate, maxDate), maxDate];
        }
        else {
            let middle = getMiddleDate(minDate, maxDate);
            dates = [
                minDate,
                getMiddleDate(minDate, middle),
                middle,
                getMiddleDate(middle, maxDate),
                maxDate,
            ];
        }
        this.d3Background
            .attr("width", width)
            .attr("height", this.areas.h)
            .style("fill", "none")
            .style("pointer-events", "all");
        this.d3Ruler.attr("y1", 0).attr("y2", this.areas.h);
        this.d3Group
            .select(".border-bottom")
            .attr("x1", 0)
            .attr("x2", width)
            .attr("y1", this.areas.h)
            .attr("y2", this.areas.h);
        this.d3Group
            .select(".border-right")
            .attr("x1", width)
            .attr("x2", width)
            .attr("y1", 0)
            .attr("y2", this.areas.h);
        let fullBand = d3
            .area()
            .curve(d3.curveBasis)
            .x((d) => {
            return x(d.date);
        })
            .y1((d) => {
            return y(d.worth);
        })
            .y0((d, i) => {
            return y(veryBadSeries[i].worth);
        });
        let mediumBand = d3
            .area()
            .curve(d3.curveBasis)
            .x((d) => {
            return x(d.date);
        })
            .y1((d) => {
            return y(d.worth);
        })
            .y0((d, i) => {
            return y(badSeries[i].worth);
        });
        let warningBand = d3
            .area()
            .curve(d3.curveBasis)
            .x((d) => {
            return x(d.date);
        })
            .y1((d) => {
            return y(d.worth);
        })
            .y0(y(startingCapital));
        this.d3Group
            .select(".full-band")
            .datum(veryGoodSeries)
            .attr("d", fullBand)
            .attr("fill", "url(#gradient)");
        this.d3Group
            .select(".medium-band")
            .datum(goodSeries)
            .attr("d", mediumBand)
            .attr("fill", "url(#gradient)");
        this.d3Group
            .select(".warning-band")
            .datum(warningSeries)
            .attr("d", warningBand);
        let xAxis = this.d3Group.select(".axis-x");
        let xAxisGroups = xAxis
            .selectAll(".axis-x-group")
            .data(dates);
        xAxisGroups.exit().remove();
        let newXAxisGroups = xAxisGroups
            .enter()
            .append("g")
            .attr("class", "axis-x-group");
        newXAxisGroups.append("line").attr("class", "axis-x-grid");
        newXAxisGroups.append("text").attr("class", "axis-x-text");
        let updateXGroup = xAxisGroups.merge(newXAxisGroups);
        updateXGroup
            .select(".axis-x-grid")
            .attr("x1", (d) => x(d))
            .attr("x2", (d) => x(d))
            .attr("y1", 0)
            .attr("y2", this.areas.h);
        updateXGroup
            .select(".axis-x-text")
            .attr("x", (d) => x(d))
            .attr("y", this.areas.h + 16)
            .attr("text-anchor", (d, i) => {
            if (i === 0) {
                return "start";
            }
            if (i === dates.length - 1) {
                return "end";
            }
            return "middle";
        })
            .text((d) => d.getFullYear());
        let yAxis = this.d3Group.select(".axis-y");
        let yAxisGroups = yAxis
            .selectAll(".axis-y-group")
            .data(ticks);
        yAxisGroups.exit().remove();
        let newYAxisGroups = yAxisGroups
            .enter()
            .append("g")
            .attr("class", "axis-y-group");
        newYAxisGroups.append("line").attr("class", "axis-y-grid");
        newYAxisGroups.append("text").attr("class", "axis-y-text");
        let updateYGroup = yAxisGroups.merge(newYAxisGroups);
        updateYGroup
            .select(".axis-y-grid")
            .attr("y1", (d) => y(d))
            .attr("y2", (d) => y(d))
            .attr("x1", 0)
            .attr("x2", (d, i) => {
            if (i === ticks.length - 1) {
                return 0;
            }
            return width;
        });
        let textOffset = 0;
        updateYGroup
            .select(".axis-y-text")
            .attr("x", textOffset)
            .attr("y", (d) => -5 + y(d))
            .attr("text-anchor", "start")
            .text((d, i) => {
            if (isMobileView) {
                return formatNumber(d / bounds.multiple, 1);
            }
            if (i % 2 === 0) {
                return formatNumber(d / bounds.multiple, 1);
            }
            return "";
        });
        this.d3Plot
            .select(".plot-vertical-entity")
            .attr("x", this.areas.w - 6)
            .attr("y", 10)
            .text("(" + bounds.ext.toUpperCase() + ")");
        if (Device.isTouchDevice()) {
            this.d3Background.on("touchmove", mouseMove);
        }
        else {
            this.d3Background.on("mousemove", mouseMove);
        }
        let lysaRuler = this.d3Ruler;
        let bisectDate = d3.bisector((d) => {
            return d.date;
        }).left;
        let tooltipArrow = tooltip.querySelector(".forecast-tooltip-arrow");
        let tooltipArrowBorder = tooltip.querySelector(".forecast-tooltip-arrow-border");
        let veryGoodCell = tooltip.querySelector(".forecast-tooltip-very-good .marker-value");
        let goodCell = tooltip.querySelector(".forecast-tooltip-good .marker-value");
        let badCell = tooltip.querySelector(".forecast-tooltip-bad .marker-value");
        let veryBadCell = tooltip.querySelector(".forecast-tooltip-very-bad .marker-value");
        let tooltipYears = tooltip.querySelector(".tooltip-years");
        let tooltipMonths = tooltip.querySelector(".tooltip-months");
        let isPortrait = Device.isMobile();
        const warning = this.tooltipIntervalWarning;
        const inner = this.tooltipInner;
        function mouseMove() {
            if (!tooltip.parentElement ||
                !tooltipYears ||
                !tooltipMonths ||
                !warning ||
                !inner ||
                !tooltipArrow ||
                !tooltipArrowBorder ||
                !veryGoodCell ||
                !goodCell ||
                !badCell ||
                !veryBadCell) {
                return;
            }
            if (Device.isTouchDevice()) {
                showHoverElements();
            }
            if (areas.w <= 0 && plot.parentElement) {
                let box = plot.parentElement.getBoundingClientRect();
                let w = box.width;
                let h = box.height;
                areas.w = w;
                areas.h = h - PLOT_BOTTOM_MARGIN - PLOT_TOP_MARGIN;
            }
            let parentContainer = tooltip.parentElement;
            let pointX = Math.max(0, d3.pointer(parentContainer)[0]);
            lysaRuler.attr("x1", pointX).attr("x2", pointX);
            const x0 = x.invert(d3.pointer(parentContainer)[0]);
            const i = bisectDate(badSeries, x0, 1);
            const d0 = badSeries[i - 1];
            const d1 = badSeries[i];
            let d = null;
            let index = 0;
            if (!d0 || !d1) {
                return;
            }
            if (x0.valueOf() - d0.date.valueOf() > d1.date.valueOf() - x0.valueOf()) {
                d = d1;
                index = i;
            }
            else {
                d = d0;
                index = i - 1;
            }
            let key = "time-" + d.date.format();
            let values = dateToWorthMap[key];
            let tooltipWidth = tooltip.getBoundingClientRect().width;
            let offset = Math.min(areas.h + 70 - y(values.veryGood), areas.h + PLOT_BOTTOM_MARGIN);
            veryBadMarker &&
                veryBadMarker.attr("transform", "translate(" + pointX + "," + y(values.veryBad) + ")");
            badMarker &&
                badMarker.attr("transform", "translate(" + pointX + "," + y(values.bad) + ")");
            goodMarker &&
                goodMarker.attr("transform", "translate(" + pointX + "," + y(values.good) + ")");
            veryGoodMarker &&
                veryGoodMarker.attr("transform", "translate(" + pointX + "," + y(values.veryGood) + ")");
            if (index < 12) {
                tooltipYears.textContent = "";
                tooltipMonths.textContent = index + " månader";
                warning.style.opacity = "1";
                inner.style.opacity = "0";
            }
            else {
                tooltipYears.textContent = Math.floor(index / 12) + " år";
                const monthRemainder = index % 12;
                tooltipMonths.textContent = " och " + monthRemainder + " månader";
                warning.style.opacity = "0";
                inner.style.opacity = "1";
            }
            tooltip.style.bottom = isPortrait
                ? areas.h + PLOT_BOTTOM_MARGIN + "px"
                : offset + "px";
            tooltipArrow.style.borderColor = "rgba(255, 255, 255, 0)";
            tooltipArrowBorder.style.borderColor = "rgba(255, 255, 255, 0)";
            if (pointX > half) {
                if (isPortrait) {
                    tooltip.style.left = "0";
                    tooltipArrow.style.left = pointX - 34 + "px";
                    tooltipArrow.style.right = "auto";
                    tooltipArrowBorder.style.left = pointX - 34 + "px";
                    tooltipArrowBorder.style.right = "auto";
                }
                else {
                    tooltip.classList.add("on-left");
                    tooltip.classList.remove("on-right");
                    tooltip.style.left = pointX - tooltipWidth + 1 + "px";
                    tooltipArrow.style.left = "auto";
                    tooltipArrow.style.right = "0";
                    tooltipArrowBorder.style.left = "auto";
                    tooltipArrowBorder.style.right = "-1px";
                }
                tooltipArrow.style.borderRightColor = "#fff";
                tooltipArrowBorder.style.borderRightColor = "#7b8994";
            }
            else {
                if (isPortrait) {
                    tooltip.style.left = "0";
                    tooltipArrow.style.left = pointX + "px";
                    tooltipArrow.style.right = "auto";
                    tooltipArrowBorder.style.left = pointX + "px";
                    tooltipArrowBorder.style.right = "auto";
                }
                else {
                    tooltip.classList.add("on-right");
                    tooltip.classList.remove("on-left");
                    tooltip.style.left = pointX + "px";
                    tooltipArrow.style.left = "0";
                    tooltipArrow.style.right = "auto";
                    tooltipArrowBorder.style.left = "-1px";
                    tooltipArrowBorder.style.right = "auto";
                }
                tooltipArrow.style.borderLeftColor = "#fff";
                tooltipArrowBorder.style.borderLeftColor = "#7b8994";
            }
            veryGoodCell.innerHTML =
                "<strong>Mer</strong> än <strong>" +
                    formatNumber(values.veryGood, 0) +
                    " SEK</strong> är <strong>10 %</strong>";
            goodCell.innerHTML =
                "<strong>Mer</strong> än <strong>" +
                    formatNumber(values.good, 0) +
                    " SEK</strong> är <strong>40 %</strong>";
            badCell.innerHTML =
                "<strong>Mindre</strong> än <strong>" +
                    formatNumber(values.bad, 0) +
                    " SEK</strong> är <strong>40 %</strong>";
            veryBadCell.innerHTML =
                "<strong>Mindre</strong> än <strong>" +
                    formatNumber(values.veryBad, 0) +
                    " SEK</strong> är <strong>10 %</strong>";
        }
        if (!this.hasAddedInputEvents) {
            if (Device.isTouchDevice()) {
                tooltip.style.opacity = "0";
                this.d3Background.on("touchmove", mouseMove);
            }
            else {
                this.d3Background.on("mousemove", mouseMove);
            }
            this.hasAddedInputEvents = true;
        }
    }
    generatePoints(props) {
        const { initial, monthly, risk, interval } = props;
        const startingCapital = parseInt(initial || "0", 10);
        const monthlySavings = parseInt(monthly || "0", 10);
        const stockPercentage = parseInt(risk || "0", 10) / 100;
        const bondsPercentage = 1 - stockPercentage;
        const investmentMonths = parseInt(interval, 10) * 12;
        const warningSeries = [];
        const veryBadSeries = [];
        const badSeries = [];
        const avgSeries = [];
        const goodSeries = [];
        const veryGoodSeries = [];
        const date = moment();
        warningSeries.push({
            worth: startingCapital,
            date: moment(date),
        });
        veryBadSeries.push({
            worth: startingCapital,
            date: moment(date),
        });
        badSeries.push({
            worth: startingCapital,
            date: moment(date),
        });
        avgSeries.push({
            worth: startingCapital,
            date: moment(date),
        });
        goodSeries.push({
            worth: startingCapital,
            date: moment(date),
        });
        veryGoodSeries.push({
            worth: startingCapital,
            date: moment(date),
        });
        let previousExpectedLogReturn = 1;
        let previousStandardDeviation = 1;
        let monthlySavingsAverage = monthlySavings;
        let monthlySavingsVeryGood = monthlySavings;
        let monthlySavingsGood = monthlySavings;
        let monthlySavingsBad = monthlySavings;
        let monthlySavingsVeryBad = monthlySavings;
        let dateToWorthMap = {};
        dateToWorthMap["time-" + moment(date).format()] = {
            veryBad: startingCapital,
            bad: startingCapital,
            average: startingCapital,
            good: startingCapital,
            veryGood: startingCapital,
        };
        for (let month = 1; month <= investmentMonths; month++) {
            date.add(1, "months");
            let expectedLogReturn = MONTHLY_BOND_RETURN * month +
                stockPercentage *
                    (MONTHLY_STOCK_RETURN * month - MONTHLY_BOND_RETURN * month) +
                0.5 * stockPercentage * bondsPercentage * month * PORTFOLIO_VOLATILITY;
            let standardDeviation = Math.sqrt(month *
                (Math.pow(bondsPercentage, 2) *
                    MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED +
                    Math.pow(stockPercentage, 2) *
                        MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED +
                    stockPercentage *
                        bondsPercentage *
                        MONTHLY_STANDARD_DEVIATION_STOCKS *
                        MONTHLY_STANDARD_DEVIATION_BONDS *
                        ASSETS_CORRELATION));
            if (monthlySavings > 0 && month > 1) {
                monthlySavingsAverage +=
                    monthlySavings * Math.exp(previousExpectedLogReturn);
                monthlySavingsVeryGood +=
                    monthlySavings *
                        Math.exp(previousExpectedLogReturn +
                            TEN_PERCENT_CONFIDENCE * previousStandardDeviation);
                monthlySavingsGood +=
                    monthlySavings *
                        Math.exp(previousExpectedLogReturn +
                            FOURTY_PERCENT_CONFIDENCE * previousStandardDeviation);
                monthlySavingsBad +=
                    monthlySavings *
                        Math.exp(previousExpectedLogReturn -
                            FOURTY_PERCENT_CONFIDENCE * previousStandardDeviation);
                monthlySavingsVeryBad +=
                    monthlySavings *
                        Math.exp(previousExpectedLogReturn -
                            TEN_PERCENT_CONFIDENCE * previousStandardDeviation);
            }
            let average = startingCapital * Math.exp(expectedLogReturn) + monthlySavingsAverage;
            let veryGood = startingCapital *
                Math.exp(expectedLogReturn + TEN_PERCENT_CONFIDENCE * standardDeviation) +
                monthlySavingsVeryGood;
            let good = startingCapital *
                Math.exp(expectedLogReturn + FOURTY_PERCENT_CONFIDENCE * standardDeviation) +
                monthlySavingsGood;
            let bad = startingCapital *
                Math.exp(expectedLogReturn - FOURTY_PERCENT_CONFIDENCE * standardDeviation) +
                monthlySavingsBad;
            let veryBad = startingCapital *
                Math.exp(expectedLogReturn - TEN_PERCENT_CONFIDENCE * standardDeviation) +
                monthlySavingsVeryBad;
            veryBadSeries.push({
                worth: veryBad,
                date: moment(date),
            });
            badSeries.push({
                worth: bad,
                date: moment(date),
            });
            avgSeries.push({
                worth: average,
                date: moment(date),
            });
            goodSeries.push({
                worth: good,
                date: moment(date),
            });
            veryGoodSeries.push({
                worth: veryGood,
                date: moment(date),
            });
            if (veryBad <= startingCapital) {
                warningSeries.push({
                    worth: veryBad,
                    date: moment(date),
                });
            }
            previousExpectedLogReturn = expectedLogReturn;
            previousStandardDeviation = standardDeviation;
            dateToWorthMap["time-" + date.format()] = {
                veryBad: veryBad,
                bad: bad,
                average: average,
                good: good,
                veryGood: veryGood,
            };
        }
        return {
            veryGoodSeries: veryGoodSeries,
            goodSeries: goodSeries,
            averageSeries: avgSeries,
            badSeries: badSeries,
            veryBadSeries: veryBadSeries,
            warningSeries: warningSeries,
            dateToWorthMap: dateToWorthMap,
            extremeValues: { min: 0, max: Number.POSITIVE_INFINITY },
        };
    }
    generateMinAndMax(props) {
        let { initial, monthly, interval } = props;
        let startingCapital = parseInt(initial || "0", 10);
        let monthlySavings = parseInt(monthly || "0", 10);
        let stockPercentage = 1;
        let bondsPercentage = 1 - stockPercentage;
        let investmentMonths = parseInt(interval, 10) * 12;
        let previousExpectedLogReturn = 1;
        let previousStandardDeviation = 1;
        let monthlySavingsVeryGood = monthlySavings;
        let veryGood = 0;
        for (let month = 1; month < investmentMonths; month++) {
            const expectedLogReturn = MONTHLY_BOND_RETURN * month +
                stockPercentage *
                    (MONTHLY_STOCK_RETURN * month - MONTHLY_BOND_RETURN * month) +
                0.5 * stockPercentage * bondsPercentage * month * PORTFOLIO_VOLATILITY;
            let standardDeviation = Math.sqrt(month *
                (Math.pow(bondsPercentage, 2) *
                    MONTHLY_STANDARD_DEVIATION_BONDS_SQUARED +
                    Math.pow(stockPercentage, 2) *
                        MONTHLY_STANDARD_DEVIATION_STOCKS_SQUARED +
                    stockPercentage *
                        bondsPercentage *
                        MONTHLY_STANDARD_DEVIATION_STOCKS *
                        MONTHLY_STANDARD_DEVIATION_BONDS *
                        ASSETS_CORRELATION));
            if (monthlySavings > 0 && month > 1) {
                monthlySavingsVeryGood +=
                    monthlySavings *
                        Math.exp(previousExpectedLogReturn +
                            TEN_PERCENT_CONFIDENCE * previousStandardDeviation);
            }
            previousExpectedLogReturn = expectedLogReturn;
            previousStandardDeviation = standardDeviation;
            veryGood =
                startingCapital *
                    Math.exp(expectedLogReturn + TEN_PERCENT_CONFIDENCE * standardDeviation) +
                    monthlySavingsVeryGood;
        }
        return {
            min: 0,
            max: veryGood,
        };
    }
}
